/**
 * La barra que acompaña al guru mientras su registro no está completo.
 *
 * El problema que resuelve: el hub tiene las tarjetas con lo que falta, pero
 * cada una expulsa al guru a otra pantalla (Mi cuenta, Disponibilidad, el
 * wizard) y ninguna lo trae de vuelta. Termina una pieza y se queda ahí, sin
 * saber si acabó ni qué sigue. Cuatro puertas, ninguna secuencia.
 *
 * Esta barra convierte eso en un camino sin reconstruir nada: vive en el shell,
 * así que sigue al guru a TODAS las pantallas, dice en qué punto va y a un clic
 * lo lleva a lo siguiente que falta. Si se sale del carril y se pone a pasear,
 * la barra sigue ahí — no lo encierra, lo orienta.
 *
 * Desaparece sola al llegar al 100%. Nadie debería seguir viendo el andamio
 * después de terminar la obra.
 */
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useGuruSetupStatus } from '@/lib/api/account';
import { useIsGuru } from '@/lib/hooks/useUserRoles';
import { guruSetupItems, siguientePendiente } from '@/lib/guru-setup-items';

export function GuruSetupBar() {
  const esGuru = useIsGuru();
  const { data: status } = useGuruSetupStatus(esGuru);
  const location = useLocation();

  if (!esGuru || !status) return null;

  const items = guruSetupItems(status);
  const listos = items.filter((i) => i.done).length;
  const siguiente = siguientePendiente(status);

  // Registro completo: fuera el andamio.
  if (!siguiente) return null;

  const pct = Math.round((listos / items.length) * 100);

  // Si el guru ya está donde tiene que llenar lo que falta, un botón que no
  // lleva a ningún lado se lee como que está roto. En ese caso el mismo espacio
  // le dice que está en el sitio correcto.
  const yaEstaAqui = `${location.pathname}${location.search}` === siguiente.to;

  return (
    <div className="border-b border-brand-accent/20 bg-brand-primary/[0.04]">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 md:px-8">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-primary">
          <Sparkles className="h-3.5 w-3.5" />
          Tu registro
        </span>

        <span className="text-xs font-medium text-brand-text-strong">
          {listos} de {items.length}
        </span>

        {/* La barra en sí. `min-w-0` + `flex-1` para que se encoja antes de
            empujar el enlace de "siguiente" a otra línea. */}
        <div
          className="h-1.5 min-w-[80px] flex-1 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progreso de tu registro"
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand-primary to-brand-accent transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>

        {yaEstaAqui ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-primary/30 bg-white px-3.5 py-1.5 text-xs font-semibold text-brand-primary">
            <span className="font-normal text-muted-foreground">Estás en:</span>
            {siguiente.label}
          </span>
        ) : (
          <Link
            to={siguiente.to}
            className="group inline-flex items-center gap-1.5 rounded-full bg-brand-primary px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-primary-light"
          >
            <span className="text-white/70">Sigue:</span>
            {siguiente.label}
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>
    </div>
  );
}
